/**
 * 67.36: wind you can see (the coordinator, Oct 10: the owner wants wind as a system on the action clock). Lane A's
 * 62.47 generator makes it a force (windHere: gusts, shelter) and the HUD's cue names it; this reads it in the world.
 * Presentation only:
 *
 *   - `windFelt` (pure): the sim's wind at a place — an acceleration, a share of the local gravity — as the eye takes
 *     it: which way along the ground it blows and how hard, 0 calm … 1 a gale (the stormiest weather's wind: the
 *     HUD's cue reads the same scale);
 *   - `WindProps`: the windsocks on the bases and the gun pits and the pennants on the masts, every one of them one
 *     InstancedMesh (one draw for the world). Each prop's swing — which way it points, how far it droops in a calm,
 *     its flutter — is done on the GPU from a few numbers per prop the CPU refreshes at its own rate (WIND_PROP.rate
 *     a second, staggered) and eases between, so a frame costs one uniform.
 *
 * The rain's slant (lane A's, 62.52) and the grit, snow and leaves blown along the ground (67.36) ride the weather's
 * fall (weatherView.ts placeFall), so they add no draw. All of it reads `windAt` (core/wind.ts: the gusting, sheltered
 * wind where it is) and is calm when the match's wind switch is off (the wind is null).
 */
import * as THREE from 'three';
import type { Vec3 } from '../types';
import { WEATHER_EFFECTS } from '../core/weather';
import { markShared } from './meshBake';

/** A gale: the stormiest weather's wind, as a share of the local gravity (decision 120's table). */
export const WIND_GALE_SHARE = Math.max(...Object.values(WEATHER_EFFECTS).map(e => e.wind));

/** The wind as the eye takes it at a place: the way it blows along the ground (a unit vector) and how hard (0 calm … 1 a gale). */
export interface WindFelt { dir: THREE.Vector3; strength: number }

/** Pure: the felt wind from the sim's wind (an acceleration; null = calm or the switch off), the ground's up and the local gravity. */
export function windFelt(wind: Vec3 | null, up: Vec3, gravity: number, out: WindFelt = { dir: new THREE.Vector3(), strength: 0 }): WindFelt {
  out.strength = 0; out.dir.set(0, 0, 0);
  if (!wind || gravity <= 0) return out;
  const a = wind.x * up.x + wind.y * up.y + wind.z * up.z;
  out.dir.set(wind.x - up.x * a, wind.y - up.y * a, wind.z - up.z * a);   // its part along the ground
  const along = out.dir.length();
  if (along / gravity < 1e-4) { out.dir.set(0, 0, 0); return out; }
  out.dir.divideScalar(along);
  out.strength = Math.min(1, along / gravity / WIND_GALE_SHARE);
  return out;
}

/** The words the HUD's cue uses, by strength. */
export function windWords(strength: number): 'calm' | 'light' | 'fresh' | 'strong' | 'a gale' {
  return strength < 0.02 ? 'calm' : strength < 0.12 ? 'light' : strength < 0.35 ? 'fresh' : strength < 0.7 ? 'strong' : 'a gale';
}

/** How fast the presentation smoke drifts downwind at a gale (person-heights a second): muzzle smoke, the vents' wisps, impact dust. */
export const WIND_SMOKE_SPEED = 2.4;
/** Pure: the drift to add to a puff's rise (world units a second), downwind with the strength; zero in a calm. */
export function smokeDrift(wind: WindFelt | null, person: number, out = new THREE.Vector3()): THREE.Vector3 {
  if (!wind || wind.strength <= 0) return out.set(0, 0, 0);
  return out.copy(wind.dir).multiplyScalar(wind.strength * WIND_SMOKE_SPEED * person);
}

/** The wind mounts under these roots that show (buildingKit windMount): a sock by every standing base and gun pit, a pennant on every standing mast. */
export function findWindMounts(roots: Iterable<THREE.Object3D>): Array<{ anchor: THREE.Object3D; kind: WindPropKind; color?: string }> {
  const out: Array<{ anchor: THREE.Object3D; kind: WindPropKind; color?: string }> = [];
  for (const r of roots) r.traverseVisible(o => { const k = o.userData.windMount; if (k === 'sock' || k === 'pennant') out.push({ anchor: o, kind: k, ...(k === 'pennant' ? { color: '#e4e0d4' } : {}) }); });
  return out;
}

/** The props' sizes (person units: a person is 1.8) and how often each reads the wind. */
export const WIND_PROP = {
  pole: 4.5, poleRadius: 0.05,
  sockLength: 1.3, sockMouth: 0.17, sockTail: 0.07, sockBands: 5,
  pennantLength: 1.8, pennantHoist: 0.55, pennantSegments: 6,
  /** wind reads a second, per prop (staggered across props) */
  rate: 4,
  /** seconds to ease most of the way to a new reading */
  ease: 0.5,
};
export type WindPropKind = 'sock' | 'pennant';

const SOCK_ORANGE = new THREE.Color('#e8642a'), SOCK_WHITE = new THREE.Color('#f2f0ea'), POLE = new THREE.Color('#3a3c44'), WHITE = new THREE.Color('#ffffff');

/**
 * One geometry for both kinds (person units), pivot at the origin: part 0 the sock's pole (straight down from the pivot),
 * part 1 the sock (an open banded cone along +x), part 2 the pennant (a tapering strip along +x). `aFlex` runs 0 at the
 * pivot to 1 at the tip. An instance shows parts 0–1 (a sock) or part 2 (a pennant); the other kind's vertices collapse.
 */
export function windPropGeometry(): THREE.BufferGeometry {
  const pos: number[] = [], nrm: number[] = [], col: number[] = [], part: number[] = [], flex: number[] = [];
  const push = (p: THREE.Vector3, n: THREE.Vector3, c: THREE.Color, pt: number, f: number) => { pos.push(p.x, p.y, p.z); nrm.push(n.x, n.y, n.z); col.push(c.r, c.g, c.b); part.push(pt); flex.push(f); };
  const tri = (a: [THREE.Vector3, THREE.Vector3], b: [THREE.Vector3, THREE.Vector3], c: [THREE.Vector3, THREE.Vector3], color: THREE.Color, pt: number, fa: number, fb: number, fc: number) => { push(a[0], a[1], color, pt, fa); push(b[0], b[1], color, pt, fb); push(c[0], c[1], color, pt, fc); };
  const W = WIND_PROP, seg = 8;
  // the pole: a thin eight-sided tube from the pivot down to the ground
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2, a1 = ((i + 1) / seg) * Math.PI * 2;
    const n0 = new THREE.Vector3(Math.cos(a0), 0, Math.sin(a0)), n1 = new THREE.Vector3(Math.cos(a1), 0, Math.sin(a1));
    const t0 = n0.clone().multiplyScalar(W.poleRadius), t1 = n1.clone().multiplyScalar(W.poleRadius), b0 = t0.clone().setY(-W.pole), b1 = t1.clone().setY(-W.pole);
    tri([t0, n0], [b0, n0], [t1, n1], POLE, 0, 0, 0, 0); tri([t1, n1], [b0, n0], [b1, n1], POLE, 0, 0, 0, 0);
  }
  // the sock: banded orange and white, an open cone from the mouth at the pivot to the tail
  for (let b = 0; b < W.sockBands; b++) {
    const x0 = (b / W.sockBands) * W.sockLength, x1 = ((b + 1) / W.sockBands) * W.sockLength;
    const r0 = W.sockMouth + (W.sockTail - W.sockMouth) * (x0 / W.sockLength), r1 = W.sockMouth + (W.sockTail - W.sockMouth) * (x1 / W.sockLength);
    const c = b % 2 === 0 ? SOCK_ORANGE : SOCK_WHITE, f0 = x0 / W.sockLength, f1 = x1 / W.sockLength;
    for (let i = 0; i < seg; i++) {
      const a0 = (i / seg) * Math.PI * 2, a1 = ((i + 1) / seg) * Math.PI * 2;
      const n0 = new THREE.Vector3(0, Math.cos(a0), Math.sin(a0)), n1 = new THREE.Vector3(0, Math.cos(a1), Math.sin(a1));
      const p00 = new THREE.Vector3(x0, n0.y * r0, n0.z * r0), p01 = new THREE.Vector3(x0, n1.y * r0, n1.z * r0);
      const p10 = new THREE.Vector3(x1, n0.y * r1, n0.z * r1), p11 = new THREE.Vector3(x1, n1.y * r1, n1.z * r1);
      tri([p00, n0], [p10, n0], [p01, n1], c, 1, f0, f1, f0); tri([p01, n1], [p10, n0], [p11, n1], c, 1, f0, f1, f1);
    }
  }
  // the pennant: a strip tapering to a point, in segments so it can wave
  const up = new THREE.Vector3(0, 0, 1);
  for (let s = 0; s < W.pennantSegments; s++) {
    const x0 = (s / W.pennantSegments) * W.pennantLength, x1 = ((s + 1) / W.pennantSegments) * W.pennantLength;
    const h0 = W.pennantHoist * (1 - x0 / W.pennantLength) / 2, h1 = W.pennantHoist * (1 - x1 / W.pennantLength) / 2;
    const f0 = x0 / W.pennantLength, f1 = x1 / W.pennantLength;
    const a = new THREE.Vector3(x0, h0, 0), b = new THREE.Vector3(x0, -h0, 0), c = new THREE.Vector3(x1, h1, 0), d = new THREE.Vector3(x1, -h1, 0);
    tri([a, up], [b, up], [c, up], WHITE, 2, f0, f0, f1); tri([c, up], [b, up], [d, up], WHITE, 2, f1, f0, f1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('aPart', new THREE.Float32BufferAttribute(part, 1));
  g.setAttribute('aFlex', new THREE.Float32BufferAttribute(flex, 1));
  return g;
}

/** The swing, on the GPU: per instance `iWind` = (yaw in the mount's frame, strength 0–1, a phase, kind 0 sock / 1 pennant). */
function windPropMaterial(time: { value: number }): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
  m.onBeforeCompile = shader => {
    shader.uniforms.uWindTime = time;
    shader.vertexShader = 'attribute vec4 iWind;\nattribute float aPart;\nattribute float aFlex;\nuniform float uWindTime;\n' + shader.vertexShader
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        float wS = iWind.y, wPen = step(0.5, iWind.w);
        mat3 wRot = mat3(1.0);
        if (aPart > 0.5) {
          float droop = mix(1.4, 0.06, smoothstep(0.03, 0.7, wS));            // hanging in a calm, out flat in a strong wind
          float c = cos(-droop), s = sin(-droop), cy = cos(iWind.x), sy = sin(iWind.x);
          wRot = mat3(cy, 0.0, -sy, 0.0, 1.0, 0.0, sy, 0.0, cy) * mat3(c, s, 0.0, -s, c, 0.0, 0.0, 0.0, 1.0);
        }
        objectNormal = wRot * objectNormal;`)
      .replace('#include <begin_vertex>', `vec3 transformed = vec3(position);
        if (aPart > 0.5) {
          float wave = sin(uWindTime * (4.0 + 8.0 * wS) + iWind.z - aFlex * mix(2.0, 6.0, wPen)) * aFlex * mix(0.03 + 0.06 * wS, 0.04 + 0.16 * wS, wPen);
          transformed.z += wave; transformed.y += wave * 0.3;
          transformed = wRot * transformed;
        }
        transformed *= 1.0 - (aPart < 1.5 ? wPen : 1.0 - wPen);`);
  };
  m.customProgramCacheKey = () => 'windProp1';
  return markShared(m);
}

interface Mount { anchor: THREE.Object3D; kind: WindPropKind; yaw: number; strength: number; yawTo: number; strengthTo: number; next: number; color: THREE.Color }

const _p = new THREE.Vector3(), _up = new THREE.Vector3(), _x = new THREE.Vector3(), _z = new THREE.Vector3(), _q = new THREE.Quaternion(), _m = new THREE.Matrix4(), _s = new THREE.Vector3(), _basis = new THREE.Matrix4();

/**
 * The windsocks and pennants of a scene: one InstancedMesh. `add` an anchor — for a sock, a point on the ground (its
 * pole stands there); for a pennant, the tip of a mast — and `update` each frame with the wind's reader. `scale` is
 * world units per person unit.
 */
export class WindProps {
  readonly mesh: THREE.InstancedMesh;
  private readonly mounts: Mount[] = [];
  private readonly wind: THREE.InstancedBufferAttribute;
  private readonly time = { value: 0 };
  private clock = 0;

  constructor(scene: THREE.Object3D, private readonly scale: number, readonly capacity = 64) {
    const g = windPropGeometry();
    this.wind = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.wind.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iWind', this.wind);
    this.mesh = new THREE.InstancedMesh(g, windPropMaterial(this.time), capacity);
    this.mesh.name = 'windProps';
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.userData.noBake = true;
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
    scene.add(this.mesh);
  }

  get size(): number { return this.mounts.length; }

  /** A prop at `anchor` (kept in the scene by its owner: it moves with it). A pennant takes `color` (its team's). */
  add(anchor: THREE.Object3D, kind: WindPropKind, color = '#ffffff'): void {
    if (this.mounts.length >= this.capacity) return;
    this.mounts.push({ anchor, kind, yaw: 0, strength: 0, yawTo: 0, strengthTo: 0, next: (this.mounts.length * 0.37) % (1 / WIND_PROP.rate), color: new THREE.Color(color) });
  }
  /** Keep exactly these mounts: new ones join, gone ones leave, the ones kept keep their swing. */
  sync(list: ReadonlyArray<{ anchor: THREE.Object3D; kind: WindPropKind; color?: string }>): void {
    const want = new Set(list.map(l => l.anchor));
    for (let i = this.mounts.length - 1; i >= 0; i--) if (!want.has(this.mounts[i].anchor)) this.mounts.splice(i, 1);
    const have = new Set(this.mounts.map(m => m.anchor));
    for (const l of list) if (!have.has(l.anchor)) this.add(l.anchor, l.kind, l.color);
  }
  remove(anchor: THREE.Object3D): void {
    const i = this.mounts.findIndex(m => m.anchor === anchor);
    if (i >= 0) this.mounts.splice(i, 1);
  }

  /**
   * Each frame: every prop placed on its anchor (its up the ground's, from `upAt`), its wind read at its own rate from
   * `feel` (null = calm) and eased toward, written for the GPU.
   */
  update(dt: number, upAt: (p: THREE.Vector3) => THREE.Vector3, feel: (p: THREE.Vector3, up: THREE.Vector3) => WindFelt | null): void {
    this.clock += dt;
    this.time.value = this.clock;
    const k = 1 - Math.exp(-dt / (WIND_PROP.ease / 3));
    const arr = this.wind.array as Float32Array, col = this.mesh.instanceColor!.array as Float32Array;
    this.mounts.forEach((m, i) => {
      m.anchor.getWorldPosition(_p);
      _up.copy(upAt(_p)).normalize();
      // a frame on the mount: up the ground's, x any tangent (the yaw is measured from it)
      _x.set(1, 0, 0); if (Math.abs(_x.dot(_up)) > 0.9) _x.set(0, 0, 1);
      _x.addScaledVector(_up, -_x.dot(_up)).normalize();
      _z.crossVectors(_x, _up);
      if (this.clock >= m.next) {
        m.next = this.clock + 1 / WIND_PROP.rate;
        const f = feel(_p, _up);
        if (f && f.strength > 0) { m.strengthTo = f.strength; m.yawTo = Math.atan2(-f.dir.dot(_z), f.dir.dot(_x)); } else m.strengthTo = 0;
      }
      m.strength += (m.strengthTo - m.strength) * k;
      const dy = Math.atan2(Math.sin(m.yawTo - m.yaw), Math.cos(m.yawTo - m.yaw));
      m.yaw += dy * k;
      // the pivot: the pole's top for a sock, the mast's tip for a pennant
      if (m.kind === 'sock') _p.addScaledVector(_up, WIND_PROP.pole * this.scale);
      _basis.makeBasis(_x, _up, _z);
      _q.setFromRotationMatrix(_basis);
      this.mesh.setMatrixAt(i, _m.compose(_p, _q, _s.setScalar(this.scale)));
      arr[i * 4] = m.yaw; arr[i * 4 + 1] = m.strength; arr[i * 4 + 2] = (i * 2.399) % (Math.PI * 2); arr[i * 4 + 3] = m.kind === 'pennant' ? 1 : 0;
      col[i * 3] = m.color.r; col[i * 3 + 1] = m.color.g; col[i * 3 + 2] = m.color.b;
    });
    this.mesh.count = this.mounts.length;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.wind.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
  }

  /** What a prop shows now (the tests read it): its yaw in its mount's frame and its strength. */
  state(i: number): { yaw: number; strength: number; kind: WindPropKind } { const m = this.mounts[i]; return { yaw: m.yaw, strength: m.strength, kind: m.kind }; }

  dispose(): void {
    this.mesh.parent?.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mesh.dispose();
  }
}
